/* eslint-disable @atlassian/tangerine/import/no-parent-imports */
import React, { useEffect, useMemo } from 'react';

import { FormattedMessage } from 'react-intl-next';

import Button from '@atlaskit/button';
import { fg } from '@atlaskit/platform-feature-flags';
import { addErrorToAll } from '@atlaskit/react-ufo/interaction-metrics';
import type { NetworkTraceInfo } from '@atlassian/search-client';

import ErrorDark from '../../../../common/constants/error/assets/error-dark.svg';
import ErrorLight from '../../../../common/constants/error/assets/error-light.svg';
import { useAppContext } from '../../../../common/ui/app-context';
import { getCachedImage } from '../../../../common/ui/image-cache';
import { ResultState } from '../../../../common/ui/result-state';
import { onErrorScreenViewed } from '../../../../common/utils/events/error-screen-viewed';
import { onFullPageSearchOperational } from '../../../../common/utils/events/full-page-search-operational';
import {
	onTaskFail,
	TASK_NAMES,
} from '../../../../common/utils/events/vital-stats-events';
import { serveSearchPageRelayExp } from '../../../../common/utils/experiments';
import type { ResultsFetchError } from '../../../../common/utils/fetch-error';
import { getExtensionData, getRelayNetworkTraceInfo } from '../../../../common/utils/relay/get-extension-data';
import { useAnalytics, useCommonAndPrivateAttributes } from '../../../../controllers/analytics';
import { usePrimaryProductStore } from '../../../../controllers/store/bootstrap';
import { useFullPageSearchVitalStats } from '../../../search-page-relay/vital-stats-context';

import { messages } from './messages';

// Global set to track which task IDs have already fired taskFail
// This prevents duplicate taskFail events when multiple Error components mount simultaneously
// The Set is cleared when a new search starts (keeping only the current task ID)
const firedTaskFailIds = new Set<string>();

// Export function to clear the Set for testing purposes
export const clearFiredTaskFailIds = (): void => {
	firedTaskFailIds.clear();
};

export type ErrorProps = {
	fetchResults: () => void;
	error?: ResultsFetchError;
};

export const Error = ({
	fetchResults,
	error,
}: ErrorProps): React.JSX.Element => {
	const vitalStats = useFullPageSearchVitalStats();
	useFireAnalytics(
		error,
		vitalStats.getUxTaskId(),
		vitalStats.onNetworkFail,
	);

	useMemo(
		() =>
			addErrorToAll(
				'search-page-results-error',
				null, // this is used internally, can be null
				'interaction error',
				error?.message || 'generic error',
			),
		[error],
	);

	const ImageErrorLight = getCachedImage('cachedImageErrorLight') || ErrorLight;
	const ImageErrorDark = getCachedImage('cachedImageErrorDark') || ErrorDark;

	return (
		<ResultState
			imageSrcLight={ImageErrorLight}
			imageSrcDark={ImageErrorDark}
			primaryText={<FormattedMessage {...messages.errorPrimary} />}
			secondaryText={
				<FormattedMessage
					{...messages.errorSecondary}
					values={{
						btn: (chunks: React.ReactNode[]) => (
							<Button appearance="link" spacing="none" onClick={fetchResults}>
								{chunks}
							</Button>
						),
					}}
				/>
			}
			testId="search-page-error"
		/>
	);
};

const useFireAnalytics = (
	error?: ResultsFetchError,
	fullPageSearchTaskId?: string | null,
	onNetworkFail?: (errorName: string, errorMessage: string, traceInfo?: NetworkTraceInfo) => void,
) => {
	const { fireAnalyticsScreenEvent, fireAnalyticsEvent } = useAnalytics();
	const { commonAttributes: commonAttrs, nonPrivacySafeAttributes } =
		useCommonAndPrivateAttributes();
	const [primaryProduct] = usePrimaryProductStore();
	const { queryParams } = useAppContext();

	useEffect(() => {
		// Prevent duplicate taskFail events for the same task ID (multiple Error components may render)
		if (fullPageSearchTaskId && firedTaskFailIds.has(fullPageSearchTaskId)) {
			return;
		}

		// Mark this task ID as having fired taskFail and clean up old task IDs
		if (fullPageSearchTaskId) {
			// Clear old task IDs to prevent unbounded memory growth
			// Keep only the current task ID (in case multiple Error components mount)
			firedTaskFailIds.clear();
			firedTaskFailIds.add(fullPageSearchTaskId);
		}
		const commonAttributes = {
			...commonAttrs,
			...(primaryProduct && serveSearchPageRelayExp(primaryProduct, false, queryParams)
				? getExtensionData(commonAttrs.isCQL)
				: {}),
		};
		fireAnalyticsScreenEvent(onErrorScreenViewed({ commonAttributes, nonPrivacySafeAttributes }));
		fireAnalyticsEvent(
			onFullPageSearchOperational({
				success: false,
				commonAttributes,
			}),
		);

		// Fire vital stats taskFail event when error screen is shown (gated by feature flag)
		if (
			fullPageSearchTaskId &&
			commonAttrs.searchSessionId &&
			fg('rovo-search-vitalstats-fullpage-search-ux')
		) {
			fireAnalyticsEvent(
				onTaskFail({
					searchSessionId: commonAttrs.searchSessionId,
					taskId: fullPageSearchTaskId,
					task: TASK_NAMES.SEARCH_PAGE_FULL_PAGE_SEARCH_RESULTS_UX,
					moreContext: {
						errorName: error?.name || 'Error',
						errorMessage: error?.message || 'generic error',
					},
				}),
			);
		}

		// Fire Network VitalStats taskFail event - gated by feature flag
		if (onNetworkFail && fg('rovo-search-vitalstats-fullpage-search-network')) {
			const traceInfo = fg('rovo-search-vitalstats-tracing-headers')
				? getRelayNetworkTraceInfo(commonAttrs.isCQL)
				: undefined;
			onNetworkFail(error?.name || 'Error', error?.message || 'generic error', traceInfo);
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, []);
};
